import { Popover, PopoverContent, PopoverTrigger } from '@crowdfpl/ui-web/popover';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { CheckCircle, ChevronDown, Circle, Star, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/helpers/utils';
import type { FeaturedCollection, ViewMode } from '@/types/featured-collections';

export interface ViewModeSelectorProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  isAuthenticated: boolean;
  onAuthRequired: () => void;
  compact?: boolean;
  collections: FeaturedCollection[];
  collectionsLoading?: boolean;
}

function isFeaturedActive(viewMode: ViewMode): boolean {
  return viewMode.type === 'featured';
}

function getCollectionPicksLabel(collection: FeaturedCollection, compact: boolean): string {
  if (!compact) {
    return collection.name;
  }
  return collection.name.replace(/\s*Picks$/i, '');
}

export function ViewModeSelector({
  viewMode,
  onViewModeChange,
  isAuthenticated,
  onAuthRequired,
  compact = false,
  collections,
  collectionsLoading = false,
}: ViewModeSelectorProps) {
  const [creatorPicksOpen, setCreatorPicksOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);

  const selectedCollection =
    viewMode.type === 'featured' ? collections.find((c) => c.slug === viewMode.collectionSlug) ?? null : null;
  const singleCollection = collections.length === 1 ? collections[0] : null;

  const showCreatorPicksSlot = collectionsLoading || collections.length > 0;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      setShowFade(el.scrollWidth > el.clientWidth && el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [collections.length, collectionsLoading]);

  const pill = (active: boolean) =>
    cn(
      'inline-flex items-center gap-1.5 !min-h-0 !min-w-0 font-semibold rounded-full border transition-all cursor-pointer select-none whitespace-nowrap shrink-0',
      compact ? 'h-8 px-3 text-[12px]' : 'h-9 px-4 text-[13px]',
      active
        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
        : 'bg-transparent text-muted-foreground border-border/60 hover:border-border hover:text-foreground hover:bg-muted/50',
    );

  const handleAuthGated = (mode: ViewMode) => {
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }
    onViewModeChange(mode);
  };

  const getPicksLabel = (): string | null => {
    if (!selectedCollection) return null;
    return getCollectionPicksLabel(selectedCollection, compact);
  };

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className={cn(
          'flex w-full min-w-0 items-center gap-1.5 overflow-x-auto py-0.5',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        <button type="button" className={pill(viewMode.type === 'all')} onClick={() => onViewModeChange({ type: 'all' })}>
          All
        </button>

        {collectionsLoading && (
          <Skeleton className={cn('shrink-0 rounded-full', compact ? 'h-8 w-[7.5rem]' : 'h-9 w-32')} />
        )}

        {!collectionsLoading && showCreatorPicksSlot && (
          <>
            <div className="hidden md:flex md:items-center md:gap-1.5">
              {collections.map((col) => (
                <button
                  key={col.id}
                  type="button"
                  className={pill(viewMode.type === 'featured' && viewMode.collectionSlug === col.slug)}
                  onClick={() => onViewModeChange({ type: 'featured', collectionSlug: col.slug })}
                >
                  {col.logo_url ? (
                    <img
                      src={col.logo_url}
                      alt=""
                      className="h-4 w-4 shrink-0 rounded-sm object-contain"
                    />
                  ) : (
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-primary-foreground/20 text-[9px] font-bold">
                      {(col.name || col.slug).slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className={cn(compact ? 'max-w-[80px]' : 'max-w-[140px]', 'truncate')}>
                    {getCollectionPicksLabel(col, compact)}
                  </span>
                </button>
              ))}
            </div>

            <div className="md:hidden">
              {singleCollection ? (
                <button
                  type="button"
                  className={pill(viewMode.type === 'featured' && viewMode.collectionSlug === singleCollection.slug)}
                  onClick={() => onViewModeChange({ type: 'featured', collectionSlug: singleCollection.slug })}
                >
                  {singleCollection.logo_url ? (
                    <img
                      src={singleCollection.logo_url}
                      alt=""
                      className="h-4 w-4 shrink-0 rounded-sm object-contain"
                    />
                  ) : (
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-primary-foreground/20 text-[9px] font-bold">
                      {(singleCollection.name || singleCollection.slug).slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className={cn(compact ? 'max-w-[80px]' : 'max-w-[120px]', 'truncate')}>
                    {getCollectionPicksLabel(singleCollection, compact)}
                  </span>
                </button>
              ) : (
                <Popover open={creatorPicksOpen} onOpenChange={setCreatorPicksOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(pill(isFeaturedActive(viewMode)), 'gap-1')}
                    >
                      {selectedCollection ? (
                        <>
                          {selectedCollection.logo_url ? (
                            <img
                              src={selectedCollection.logo_url}
                              alt=""
                              className="h-4 w-4 shrink-0 rounded-sm object-contain"
                            />
                          ) : (
                            <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-primary-foreground/20 text-[9px] font-bold">
                              {(selectedCollection.name || selectedCollection.slug).slice(0, 2).toUpperCase()}
                            </span>
                          )}
                          <span className={cn(compact ? 'max-w-[80px]' : 'max-w-[120px]', 'truncate')}>
                            {getPicksLabel()}
                          </span>
                        </>
                      ) : (
                        <>
                          <Star className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          <span>Creator Picks</span>
                        </>
                      )}
                      <ChevronDown
                        className={cn('h-3 w-3 shrink-0 transition-transform', creatorPicksOpen && 'rotate-180')}
                        aria-hidden
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-52 min-w-52 p-1" align="start" portal>
                    {collections.map((col) => (
                      <CollectionItem
                        key={col.id}
                        collection={col}
                        isSelected={viewMode.type === 'featured' && viewMode.collectionSlug === col.slug}
                        onSelect={() => {
                          onViewModeChange({ type: 'featured', collectionSlug: col.slug });
                          setCreatorPicksOpen(false);
                        }}
                      />
                    ))}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </>
        )}

        <div className="h-4 w-px shrink-0 bg-border/40" aria-hidden />

        <button
          type="button"
          className={pill(viewMode.type === 'my-unvoted')}
          onClick={() => handleAuthGated({ type: 'my-unvoted' })}
        >
          <Circle className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
          Unvoted
        </button>

        <button
          type="button"
          className={pill(viewMode.type === 'my-voted')}
          onClick={() => handleAuthGated({ type: 'my-voted' })}
        >
          <CheckCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Voted
        </button>

        <button
          type="button"
          className={pill(viewMode.type === 'my-duels')}
          onClick={() => handleAuthGated({ type: 'my-duels' })}
        >
          <User className="h-3.5 w-3.5 shrink-0" aria-hidden />
          My&nbsp;Duels
        </button>
      </div>

      {showFade && (
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 w-8 bg-gradient-to-l from-background to-transparent" />
      )}
    </div>
  );
}

function CollectionItem({
  collection,
  isSelected,
  onSelect,
}: {
  collection: FeaturedCollection;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex w-full min-w-0 items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors !min-h-0',
        isSelected ? 'bg-primary/10 font-medium text-foreground' : 'text-foreground/80 hover:bg-muted/60',
      )}
      onClick={onSelect}
    >
      {collection.logo_url ? (
        <img src={collection.logo_url} alt="" className="h-5 w-5 shrink-0 rounded-sm object-contain" />
      ) : (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-muted text-[10px] font-semibold">
          {(collection.name || collection.slug).slice(0, 1).toUpperCase()}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{collection.name}</span>
        <span className="text-[10px] text-muted-foreground">
          {collection.duel_count} {collection.duel_count === 1 ? 'duel' : 'duels'}
        </span>
      </div>
      {isSelected && <CheckCircle className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />}
    </button>
  );
}
